import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import AdminHeader from "../components/AdminHeader";
import { Gamepad2, Plus, Trash2, Power, Wifi, WifiOff, Settings2, Search, Timer, Ban } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

export default function GamesControl() {
  const { isAdmin } = useAuth();
  const [devices, setDevices] = useState([]);
  const [config, setConfig] = useState({ configured: false, demo: true });
  const [busy, setBusy] = useState(null);
  const [remaining, setRemaining] = useState({});
  const [manage, setManage] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", device_id: "", switch_code: "switch", allow_staff: true });
  const [discovered, setDiscovered] = useState(null);

  const load = useCallback(async () => {
    try {
      const [g, c] = await Promise.all([api.get("/games"), api.get("/games/config")]);
      setDevices(g.data);
      setConfig(c.data);
      setRemaining((prev) => {
        const m = { ...prev };
        g.data.forEach((d) => { if (typeof d.countdown === "number") m[d.id] = d.countdown; });
        return m;
      });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => {
      setRemaining((prev) => {
        const m = {};
        for (const k in prev) m[k] = prev[k] > 0 ? prev[k] - 1 : 0;
        return m;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const toggle = async (d) => {
    setBusy(d.id);
    try {
      const res = await api.post(`/games/${d.id}/toggle`, { on: !d.on });
      setDevices((prev) => prev.map((x) => (x.id === d.id ? { ...x, on: res.data.on, online: res.data.online } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not switch ${d.name}`);
      load();
    } finally { setBusy(null); }
  };

  const COUNTDOWN_SECS = 300;
  const startCountdown = async (d) => {
    try {
      const res = await api.post(`/games/${d.id}/countdown`, { seconds: COUNTDOWN_SECS });
      setDevices((prev) => prev.map((x) => (x.id === d.id ? { ...x, on: true } : x)));
      setRemaining((prev) => ({ ...prev, [d.id]: res.data.countdown || COUNTDOWN_SECS }));
      toast.success(`${d.name}: auto-off in 5:00`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start timer");
    }
  };
  const cancelCountdown = async (d) => {
    try {
      await api.post(`/games/${d.id}/countdown`, { seconds: 0 });
      setRemaining((prev) => ({ ...prev, [d.id]: 0 }));
      toast.success(`${d.name}: timer cancelled`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel timer");
    }
  };
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const addDevice = async () => {
    if (!form.name.trim()) { toast.error("Enter a name"); return; }
    await api.post("/games", form);
    toast.success("Device added");
    setOpen(false);
    setForm({ name: "", device_id: "", switch_code: "switch", allow_staff: true });
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.name}"?`)) return;
    await api.delete(`/games/${d.id}`);
    load();
  };

  const discover = async () => {
    try {
      const res = await api.get("/games/discover");
      setDiscovered(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Discovery failed"); }
  };

  const inputCls = "w-full h-12 rounded-lg bg-[#09090B] border border-white/10 px-3 text-white outline-none focus:border-[#FF007F]";

  return (
    <div className="min-h-screen bg-[#09090B] p-4 md:p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <AdminHeader title="GAMES CONTROL" accent="#00F0FF" />
        {isAdmin && (
          <button data-testid="games-manage-toggle" onClick={() => setManage((m) => !m)}
            className={`h-14 px-5 rounded-xl font-display font-bold flex items-center gap-2 active:scale-95 transition-transform duration-75 ${manage ? "bg-[#FF007F] text-white" : "bg-[#27272A] text-white border border-white/10"}`}>
            <Settings2 className="w-5 h-5" /> {manage ? "DONE" : "MANAGE"}
          </button>
        )}
      </div>

      {config.demo && (
        <div data-testid="games-demo-banner" className="mb-5 rounded-2xl border border-[#FFCC00]/40 bg-[#FFCC00]/10 text-[#FFCC00] px-5 py-4 font-semibold">
          DEMO MODE — toggles work on-screen but no real plug is connected yet. Add your Tuya credentials to go live.
        </div>
      )}

      {/* Manage / add controls */}
      {isAdmin && manage && (
        <div className="mb-5 flex gap-3 flex-wrap">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-game-button"
                className="h-14 px-5 rounded-xl bg-[#00FA9A] text-black font-display font-black tracking-wide flex items-center gap-2 active:scale-95 transition-transform duration-75">
                <Plus className="w-6 h-6" /> ADD DEVICE
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181B] border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Add Games Device</DialogTitle>
                <DialogDescription className="text-[#71717A]">Give the plug a name staff will recognise.</DialogDescription>
              </DialogHeader>
              <label className="block mb-3">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Name</span>
                <input data-testid="game-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Arcade Machine" />
              </label>
              <label className="block mb-3">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Tuya Device ID (leave blank for demo)</span>
                <input data-testid="game-deviceid-input" className={inputCls} value={form.device_id} onChange={(e) => setForm({ ...form, device_id: e.target.value })} placeholder="bfxxxxxxxxxxxxxxxxxx" />
              </label>
              <label className="block mb-3">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Switch Code</span>
                <input data-testid="game-switchcode-input" className={inputCls} value={form.switch_code} onChange={(e) => setForm({ ...form, switch_code: e.target.value })} placeholder="switch" />
              </label>
              <label className="flex items-center gap-3 cursor-pointer mb-2">
                <input type="checkbox" checked={form.allow_staff} onChange={(e) => setForm({ ...form, allow_staff: e.target.checked })} className="w-5 h-5 accent-[#FF007F]" />
                <span className="text-sm text-white">Staff can control this device</span>
              </label>
              <DialogFooter>
                <button data-testid="submit-game-button" onClick={addDevice}
                  className="w-full h-14 rounded-xl bg-[#FF007F] text-white font-display font-black text-lg active:scale-95 transition-transform duration-75">SAVE DEVICE</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {config.configured && (
            <button data-testid="discover-button" onClick={discover}
              className="h-14 px-5 rounded-xl bg-[#27272A] text-white border border-white/10 font-display font-bold flex items-center gap-2 active:scale-95 transition-transform duration-75">
              <Search className="w-5 h-5" /> DISCOVER PLUGS
            </button>
          )}
        </div>
      )}

      {discovered && (
        <div className="mb-5 bg-[#18181B] border border-white/10 rounded-2xl p-4">
          <div className="font-bold text-white mb-2">Tuya devices on your account (copy the ID into a device):</div>
          {discovered.length === 0 && <div className="text-[#71717A]">No devices found — check region & account linking.</div>}
          {discovered.map((d) => (
            <div key={d.device_id} className="flex justify-between py-1 text-sm">
              <span className="text-white">{d.name}</span>
              <span className="text-[#71717A] font-mono">{d.device_id}</span>
            </div>
          ))}
        </div>
      )}

      {/* Device grid */}
      {devices.length === 0 ? (
        <div className="bg-[#18181B] border border-white/10 rounded-2xl p-10 text-center">
          <Gamepad2 className="w-12 h-12 text-[#3f3f46] mx-auto mb-3" />
          <p className="font-display font-black text-2xl text-white mb-1">NO DEVICES YET</p>
          <p className="text-[#71717A]">{isAdmin ? "Tap MANAGE → ADD DEVICE to get started." : "An administrator can add game devices."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {devices.map((d) => {
            const offline = d.online === false;
            return (
              <div key={d.id} data-testid={`game-card-${d.id}`}
                className={`rounded-2xl p-6 border transition-colors duration-200 ${d.on ? "bg-[#FF007F]/15 border-[#FF007F]" : "bg-[#18181B] border-white/10"}`}>
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${d.on ? "bg-[#FF007F] text-white" : "bg-[#27272A] text-[#A1A1AA]"}`}>
                      <Gamepad2 className="w-8 h-8" />
                    </div>
                    <div>
                      <div className="font-display font-black text-2xl text-white leading-none">{d.name}</div>
                      <div className={`text-sm font-bold mt-1 flex items-center gap-1 ${d.on ? "text-[#FF007F]" : "text-[#71717A]"}`}>
                        {d.online === false ? <><WifiOff className="w-4 h-4" /> OFFLINE</> :
                         d.online === true ? <><Wifi className="w-4 h-4" /> {d.on ? "ON" : "OFF"}</> :
                         (d.on ? "ON" : "OFF")}
                      </div>
                    </div>
                  </div>
                  {isAdmin && manage && (
                    <button data-testid={`delete-game-${d.id}`} onClick={() => remove(d)}
                      className="w-10 h-10 rounded-lg bg-[#27272A] hover:bg-[#FF3B30] text-white flex items-center justify-center active:scale-95 transition-transform duration-75">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <button
                  data-testid={`game-toggle-${d.id}`}
                  disabled={busy === d.id || offline}
                  onClick={() => toggle(d)}
                  className={`w-full h-24 rounded-2xl font-display font-black text-3xl tracking-wide flex items-center justify-center gap-3 active:scale-95 transition-transform duration-75 disabled:opacity-40 ${
                    d.on ? "bg-[#FF007F] text-white" : "bg-[#27272A] text-white border border-white/10"
                  }`}
                >
                  <Power className="w-9 h-9" />
                  {d.on ? "TURN OFF" : "TURN ON"}
                </button>
                {remaining[d.id] > 0 ? (
                  <div data-testid={`game-countdown-active-${d.id}`} className="mt-3 flex items-center gap-3">
                    <div className="flex-1 h-14 rounded-xl bg-[#00F0FF]/15 border border-[#00F0FF] text-[#00F0FF] flex items-center justify-center gap-2 font-display font-black text-xl">
                      <Timer className="w-6 h-6" /> AUTO-OFF {fmt(remaining[d.id])}
                    </div>
                    <button data-testid={`game-countdown-cancel-${d.id}`} onClick={() => cancelCountdown(d)}
                      className="h-14 px-4 rounded-xl bg-[#27272A] text-white border border-white/10 font-display font-bold flex items-center gap-2 active:scale-95 transition-transform duration-75">
                      <Ban className="w-5 h-5" /> CANCEL
                    </button>
                  </div>
                ) : (
                  <button data-testid={`game-countdown-${d.id}`} disabled={offline} onClick={() => startCountdown(d)}
                    className="mt-3 w-full h-14 rounded-xl bg-[#27272A] text-white border border-white/10 font-display font-bold text-lg flex items-center justify-center gap-2 active:scale-95 transition-transform duration-75 disabled:opacity-40">
                    <Timer className="w-6 h-6 text-[#00F0FF]" /> 5 MIN → OFF
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
